import type { Locale } from '@itin/shared'
import { useLocale, useT } from '../lib/i18n.js'

/**
 * "This is the copy saved on your phone, from Tuesday at 18:40."
 *
 * The whole point of the offline work, and the part that could most easily have
 * been left out. An itinerary served from a cache looks exactly like one served
 * from the server — same layout, same confident times — and the difference only
 * becomes visible to the traveller at the moment it costs them something, which
 * is standing at a gate reading a departure that moved yesterday. A cached copy
 * that does not say it is a copy is worse than no offline support at all,
 * because without it the page at least fails honestly.
 *
 * Renders nothing when the plan came off the network, which is almost always.
 */
export function OfflineNotice({ storedAt }: { storedAt: string | null }) {
  const t = useT()
  const { locale } = useLocale()

  if (!storedAt) return null

  return (
    <aside
      data-testid="offline-copy"
      className="rounded-lg border border-slate-300 bg-slate-50 px-4 py-3"
    >
      {/*
        NOT `no-print`, unlike every other banner on this page.

        A printed itinerary that came off a saved copy is the single most
        dangerous artefact this product can produce: it leaves the browser, gets
        forwarded on WhatsApp, goes in a folder, and carries no clue that it was
        never refreshed. The date it was saved is exactly what belongs on it.
      */}
      <p className="text-sm font-medium text-slate-900">
        {t('offline.storedCopy', { when: formatStoredAt(storedAt, locale) })}
      </p>
      {/* This half is about one phone's signal in one minute and is meaningless
          on paper. */}
      <p className="no-print mt-0.5 text-xs leading-snug text-slate-500">
        {t('offline.mayHaveChanged')}
      </p>
    </aside>
  )
}

/**
 * The device's own clock, deliberately.
 *
 * PATTERNS §3b is emphatic that a booking must never be read in the browser's
 * zone, and this is the exception that proves the rule rather than a breach of
 * it: what is being shown is not a time on the trip's clock, it is the moment
 * this phone last managed to reach the server. That happened on this phone, so
 * the reader's own clock is the only frame in which "yesterday at 18:40" means
 * what they will take it to mean. Rendering it on the trip's zone would be the
 * plausible wrong answer §3b warns about, one storey up: a traveller in Paris
 * would be told their copy was saved at a time they were asleep.
 */
function formatStoredAt(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  )
}
